import { FormattedMessage } from 'react-intl';
import { NavLink } from 'react-router';

import { DiscountBadgeWithTooltip } from '../../../common/discount/DiscountBadge';
import type { ProductDiscount } from '../../../common/discount/redux/types';
import { getCustomerCenterPath } from '../../../common/routes';
import { ContractPeriod } from '../../../common/termedLevel/ContractPeriod';
import { type Period, PeriodUnit } from '../../../common/termedLevel/types';
import { OverlayTrigger, Tooltip } from '../../../common/utils/tooltipReExports';
import { CustomerCenterTab } from '../../types';

interface Props {
    period: Period | undefined;
    freeTrial: boolean;
    integratedOffer: boolean;
    legacy: boolean;
    discount: ProductDiscount | undefined;
}

function getFreeTrialDividerText() {
    return (
        <NavLink className={'btn btn-secondary btn-sm'} to={`${getCustomerCenterPath(CustomerCenterTab.FREE_TRIAL)}`}>
            <FormattedMessage id='marketplace.free.trial' />
        </NavLink>
    );
}

function getIntegratedOfferDividerText() {
    return <FormattedMessage id='marketplace.integrated.offer.divider' />;
}

function getLegacyInfoIcon() {
    return (
        <>
            {' '}
            <OverlayTrigger
                placement='top'
                overlay={
                    <Tooltip>
                        <FormattedMessage id='marketplace.product.legacy.tooltip' />
                    </Tooltip>
                }
            >
                <span className={'uikit-icon icon-info-sign'} />
            </OverlayTrigger>
        </>
    );
}

function getPeriodDividerText(period: Period) {
    const isP1DContract = period.length === 1 && period.unit === PeriodUnit.DAY;

    if (isP1DContract) {
        return <FormattedMessage id='marketplace.p1d.period.label' />;
    }

    return <ContractPeriod contractPeriod={period} highlightPeriod={true} />;
}

function getDividerText(period: Period | undefined, freeTrialLevel: boolean, integratedOfferLevel: boolean) {
    if (freeTrialLevel) {
        return getFreeTrialDividerText();
    }

    if (integratedOfferLevel) {
        return getIntegratedOfferDividerText();
    }

    if (period) {
        return getPeriodDividerText(period);
    }

    return <FormattedMessage id='marketplace.productDetails.evergreen' />;
}

export const LevelDivider = ({ period, freeTrial, integratedOffer, legacy, discount }: Props) => {
    const dividerText = getDividerText(period, freeTrial, integratedOffer);
    const legacyInfoIcon = legacy ? getLegacyInfoIcon() : undefined;

    return (
        <div className='position-relative padding-top-15 padding-bottom-15'>
            <hr className='margin-0 border-width-2 margin-left-15 margin-right-15' />
            <span className='bg-lightest padding-10 text-size-h6 position-absolute top-50pct left-50pct translate-x-50pct-y-50pct'>
                {dividerText}
                {legacyInfoIcon}
                {discount && (
                    <>
                        <span>&nbsp;</span>
                        <DiscountBadgeWithTooltip discount={discount} />
                    </>
                )}
            </span>
        </div>
    );
};
